import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ContentLoader from 'react-content-loader';

import { formatRoute } from 'react-router-named-routes';
// graphQL
import gql from 'graphql-tag';
// adopt
import { adopt } from 'react-adopt';
// our components
import { USER } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import { Query, Mutation } from 'components/common/Query';
import { PageInfo } from 'components/common/Paginator';
import UserPermission from './Permission';

/**
 * Query to fetch user
 */
export const ADMIN_USER_QUERY = gql`
  query AdminUser($id: ID!) {
    adminUser(id: $id) {
      id
      fullname
      photo {
        gcsKey
        url
      }
      permissions {
        id
        name
      }
      permissionsGroups {
        id
        name
        permissions {
          id
          name
        }
      }
    }
  }
`;

/**
 * Query to fetch permissions groups
 */
export const PERMISSIONS_GROUP_QUERY = gql`
  query PermissionsGroupsList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $name: String
  ) {
    permissionsGroups(
      first: $first
      after: $after
      last: $last
      before: $before
      name: $name
    ) {
      pageInfo {
        ...PaginatorPageInfo
      }
      edges {
        node {
          id
          name
          permissions {
            id
            name
          }
        }
      }
    }
  }
  ${PageInfo}
`;

/**
 * Mutation to update user
 */
export const UPDATE_USER_MUTATION = gql`
  mutation($id: ID, $permissionIds: [Int], $permissionGroupIds: [Int]) {
    updateAdminUser(
      input: {
        id: $id
        permissionIds: $permissionIds
        permissionGroupIds: $permissionGroupIds
      }
    ) {
      errors {
        field
        messages
      }
      clientMutationId
    }
  }
`;

/**
 * @class
 * @extends Component
 */
class UserPermissionContainer extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.Compose = adopt({
      mutateUpdate: this.updateUser,
      queryAdminUser: this.user,
      queryPermissionsGroups: this.permissionsGroups,
    });
  }

  /**
   * @description
   * Mutation update callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  updateUser = ({ render }) => (
    <Mutation
      mutation={UPDATE_USER_MUTATION}
      onCompleted={this.mutationUpdateCompleted}
    >
      {(handleUpdate, { loading }) =>
        render({ handleUpdate, loadingUpdate: loading })
      }
    </Mutation>
  );

  /**
   * @description
   * Query callback to compose with another query.
   *
   * @param {*} render - Query's render props.
   */
  user = ({ render }) => {
    const { id } = this.props;
    return (
      <Query
        query={ADMIN_USER_QUERY}
        variables={{ id }}
        notifyOnNetworkStatusChange
        fetchPolicy="cache-and-network"
      >
        {({ data, loading }) =>
          render({ userData: data, loadingUser: loading })
        }
      </Query>
    );
  };

  /**
   * @description
   * Query callback to compose with another query.
   *
   * @param {*} render - Query's render props.
   * @returns {*} Query component.
   */
  permissionsGroups = ({ render }) => (
    <Query
      query={PERMISSIONS_GROUP_QUERY}
      variables={{}}
      notifyOnNetworkStatusChange
      fetchPolicy="cache-and-network"
    >
      {({ data, loading }) =>
        render({
          permissionsGroupsData: data,
          loadingPermissionsGroups: loading,
        })
      }
    </Query>
  );

  /**
   * @description
   * OnComplete update mutation.
   *
   * @param {Object} updateAdminUser - Mutation result.
   * @returns {*} Mutation component.
   */
  mutationUpdateCompleted = async ({ updateAdminUser }) => {
    const { id } = this.props;
    const history = this.context;
    const { errors } = updateAdminUser;
    if (!errors) {
      history.push(formatRoute(`${USER.DETAILS}#permissions`, { id }));
    }
  };

  /**
   * @description
   * Render UserPermission component.
   *
   * @returns {*} UserPermission component.
   */
  render() {
    const { id } = this.props;
    return (
      <this.Compose>
        {({
          mutateUpdate: { handleUpdate, loadingUpdate },
          queryAdminUser: { userData, loadingUser },
          queryPermissionsGroups: {
            permissionsGroupsData,
            loadingPermissionsGroups,
          },
        }) => {
          if (loadingUser || loadingPermissionsGroups || loadingUpdate)
            return (
              <ContentLoader
                height={519}
                width={912}
                speed={2}
                primaryColor="#d9d9d9"
                secondaryColor="#ecebeb"
              >
                <path
                  // eslint-disable-next-line max-len
                  d="M5.5,95 L123.5,95 C126.537566,95 129,97.4624339 129,100.5 C129,103.537566 126.537566,106 123.5,106 L5.5,106 C2.46243388,106 3.71994563e-16,103.537566 0,100.5 C-3.71994563e-16,97.4624339 2.46243388,95 5.5,95 Z M8,122 L194,122 C198.418278,122 202,125.581722 202,130 L202,385 C202,389.418278 198.418278,393 194,393 L8,393 C3.581722,393 5.41083001e-16,389.418278 0,385 L0,130 C-5.41083001e-16,125.581722 3.581722,122 8,122 Z M271,95 L520,95 C524.418278,95 528,98.581722 528,103 L528,511 C528,515.418278 524.418278,519 520,519 L271,519 C266.581722,519 263,515.418278 263,511 L263,103 C263,98.581722 266.581722,95 271,95 Z M616,285 C620.418278,285 624,288.581722 624,293 L624,321 C624,325.418278 620.418278,329 616,329 L560,329 C555.581722,329 552,325.418278 552,321 L552,293 C552,288.581722 555.581722,285 560,285 L616,285 Z M655,95 L904,95 C908.418278,95 912,98.581722 912,103 L912,511 C912,515.418278 908.418278,519 904,519 L655,519 C650.581722,519 647,515.418278 647,511 L647,103 C647,98.581722 650.581722,95 655,95 Z M7,10 L207,10 C210.865993,10 214,13.1340068 214,17 L214,34 C214,37.8659932 210.865993,41 207,41 L7,41 C3.13400675,41 4.73447626e-16,37.8659932 0,34 L-8.8817842e-16,17 C-1.36162605e-15,13.1340068 3.13400675,10 7,10 Z M781,17 L904,17 C908.418278,17 912,20.581722 912,25 C912,29.418278 908.418278,33 904,33 L781,33 C776.581722,33 773,29.418278 773,25 C773,20.581722 776.581722,17 781,17 Z M734,0 C747.807119,-2.53632657e-15 759,11.1928813 759,25 C759,38.8071187 747.807119,50 734,50 C720.192881,50 709,38.8071187 709,25 C709,11.1928813 720.192881,2.53632657e-15 734,0 Z"
                  id="Combined-Shape-Copy"
                  fill="#E5EAEC"
                />
              </ContentLoader>
            );
          return (
            <UserPermission
              user={userData.adminUser}
              groups={permissionsGroupsData.permissionsGroups}
              permissionUpdate={permissions =>
                handleUpdate({
                  variables: {
                    id,
                    ...permissions,
                  },
                })
              }
            />
          );
        }}
      </this.Compose>
    );
  }
}

/**
 * UserPermissionContainer PropTypes
 * @param id - prop to know User Id
 */

UserPermissionContainer.propTypes = {
  id: PropTypes.string.isRequired,
};

export default UserPermissionContainer;
